// The observations bedside monitor: the one dark object on the light page, laid out and coloured like a ward monitor
// (heart rate in bpm and the ECG in green, SpO2 and its pleth wave in cyan, NIBP in white, respiratory rate in yellow).
// A live ECG trace at the patient's rate and a pleth wave from the same beats sweep across the screen, each sized to
// its own lane below its label, as a monitor's auto-size does; numerals are set in the print font and count up
// when the monitor appears. Parameters the item does not give are left off rather than shown as dashes, which a
// player could read as a clue. The monitor shows readings only and never a score: NEWS2 came off it on 8 Oct 2026
// on MedSnapp's ruling (a nursing task, not what medical students are tested on). With `pick` (v2.2, tap the
// observation) every reading the question offers is a button, oxygen gets a reading of its own (air or on oxygen),
// and at the reveal the right readings are ringed in green, a wrong pick in red, with the room's share under each.
import { useEffect, useRef, type RefObject } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import type { ObsGraphic, Rhythm } from '@shared/types';
import { createEcgStream } from './ecg/synth';
import type { EcgParams } from './ecg/types';
import { startSweep } from './monitor/sweep';
import { plethAt } from './monitor/pleth';
import { autoSize, traceExtent } from './monitor/fit';
import { cssToken } from './env';
import { useCountUps } from './countUp';
import { chem } from './chem';
import type { SlipRowState } from './BloodsSlip';
import './clinical.css';

/** The observations a player can tap, as the contract names them. */
export type ObsParam = 'rr' | 'spo2' | 'o2' | 'sbp' | 'hr' | 'consciousness' | 'temp';
export interface ObsRegion { /** The answer option id. */ id: string; param: ObsParam }
export interface ObsPick {
  regions: ReadonlyArray<ObsRegion>;
  stateOf: (id: string) => SlipRowState;
  disabled?: boolean;
  onPick?: (id: string) => void;
  /** At the reveal: the share of the room that tapped each reading. */
  pctOf?: (id: string) => number;
  /** The option this player tapped. */
  mine?: string;
}

type Consciousness = NonNullable<ObsGraphic['consciousness']>;
const ACVPU_LETTER: Record<Consciousness, string> = { alert: 'A', 'new-confusion': 'C', voice: 'V', pain: 'P', unresponsive: 'U' };
const ACVPU_WORD: Record<Consciousness, string> = { alert: 'alert', 'new-confusion': 'new confusion', voice: 'voice', pain: 'pain', unresponsive: 'unresponsive' };
/** How much room each reading needs in the bottom row. */
const WEIGHT: Record<string, number> = { spo2: 1.1, nibp: 1.6, rr: 1, temp: 1.15, acvpu: 1, o2: 1 };
const ACVPU_SPOKEN: Record<Consciousness, string> = { alert: 'alert', 'new-confusion': 'new confusion', voice: 'responds to voice', pain: 'responds to pain', unresponsive: 'unresponsive' };

const clamp = (x: number, lo: number, hi: number) => (x < lo ? lo : x > hi ? hi : x);
const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** What the monitor says aloud: the readings in order, and nothing else. */
export function monitorLabel(o: ObsGraphic): string {
  const bits: string[] = ['Bedside observations.'];
  if (num(o.hr)) bits.push(`Heart rate ${Math.round(o.hr)} per minute.`);
  if (num(o.spo2)) bits.push(`Oxygen saturation ${o.spo2} percent${o.onO2 === true ? ' on oxygen' : o.onO2 === false ? ' on air' : ''}.`);
  if (num(o.sbp)) bits.push(num(o.dbp) ? `Blood pressure ${o.sbp} over ${o.dbp}.` : `Systolic blood pressure ${o.sbp}.`);
  if (num(o.rr)) bits.push(`Respiratory rate ${o.rr} per minute.`);
  if (num(o.temp)) bits.push(`Temperature ${o.temp.toFixed(1)} degrees.`);
  if (o.consciousness) bits.push(`Consciousness: ${ACVPU_SPOKEN[o.consciousness]}.`);
  return bits.join(' ');
}

interface Cell { p: 'hr' | 'spo2' | 'nibp' | 'rr' | 'temp' | 'acvpu' | 'o2'; label: string; sub?: string; values: string[]; joiner?: string; unit?: string; map?: string; text?: boolean; note?: string; region: ObsParam; spoken: string }

export interface ObsMonitorProps {
  graphic: ObsGraphic;
  /** The answer has been revealed: the numerals no longer count up and, with `pick`, the right readings are marked. */
  revealed?: boolean;
  /** The rhythm to draw (see monitor/rhythm.ts); sinus at the card's rate when left out. */
  rhythm?: Rhythm;
  /** Tap the observation: makes the readings the question offers tappable. */
  pick?: ObsPick;
}

export function ObsMonitor({ graphic: o, revealed = false, rhythm, pick }: ObsMonitorProps) {
  const hasHr = num(o.hr) && o.hr > 0;
  const hasSpo2 = num(o.spo2);
  const regionFor = new Map((pick?.regions ?? []).map((r) => [r.param, r]));
  // Oxygen is its own reading when the question lets players tap it; otherwise it rides on the saturation.
  const o2Cell = typeof o.onO2 === 'boolean' && regionFor.has('o2');

  const side: Cell[] = [];
  const foot: Cell[] = [];
  const hrCell: Cell | undefined = hasHr ? { p: 'hr', region: 'hr', label: 'HR', sub: 'bpm', values: [String(Math.round(o.hr!))], spoken: `Heart rate ${Math.round(o.hr!)} per minute` } : undefined;
  const spo2Cell: Cell | undefined = hasSpo2
    ? { p: 'spo2', region: 'spo2', label: 'SpO2', sub: o2Cell ? undefined : o.onO2 === true ? 'on O2' : o.onO2 === false ? 'air' : undefined, values: [String(o.spo2)], unit: '%', spoken: `Oxygen saturation ${o.spo2} percent` }
    : undefined;
  if (hasHr) { side.push(hrCell!); if (spo2Cell) side.push(spo2Cell); }
  else if (spo2Cell) foot.push(spo2Cell);
  if (num(o.sbp)) foot.push({ p: 'nibp', region: 'sbp', label: 'NIBP', sub: 'mmHg', values: num(o.dbp) ? [String(o.sbp), String(o.dbp)] : [String(o.sbp)], joiner: '/', map: num(o.dbp) ? `(${Math.round((o.sbp + 2 * o.dbp) / 3)})` : undefined, spoken: num(o.dbp) ? `Blood pressure ${o.sbp} over ${o.dbp}` : `Systolic blood pressure ${o.sbp}` });
  if (num(o.rr)) foot.push({ p: 'rr', region: 'rr', label: 'RR', sub: '/min', values: [String(o.rr)], spoken: `Respiratory rate ${o.rr} per minute` });
  if (num(o.temp)) foot.push({ p: 'temp', region: 'temp', label: 'Temp', sub: '°C', values: [o.temp.toFixed(1)], spoken: `Temperature ${o.temp.toFixed(1)} degrees` });
  if (o.consciousness) foot.push({ p: 'acvpu', region: 'consciousness', label: 'ACVPU', values: [ACVPU_LETTER[o.consciousness]], text: true, note: ACVPU_WORD[o.consciousness], spoken: `Consciousness: ${ACVPU_SPOKEN[o.consciousness]}` });
  if (o2Cell) foot.push({ p: 'o2', region: 'o2', label: 'Air/O2', values: [o.onO2 ? 'O2' : 'Air'], text: true, spoken: o.onO2 ? 'On supplemental oxygen' : 'Breathing room air' });

  // Count the numerals up once, when the monitor first appears (not again at the reveal).
  const numberRefs = useRef<Array<HTMLElement | null>>([]);
  let next = 0;
  const slotted = [...side, ...foot].map((cell) => { const base = next; if (!cell.text) next += cell.values.length; return { cell, base }; });
  const targets = slotted.flatMap(({ cell }) => (cell.text ? [] : cell.values)).map((text, i) => ({ text, delayMs: 120 + i * 70 }));
  const runKey = targets.map((t) => t.text).join('|');
  useCountUps(numberRefs, targets, 560, runKey, !revealed);
  const sideCells = slotted.slice(0, side.length);
  const footCells = slotted.slice(side.length);

  const picking = Boolean(pick && pick.regions.length);
  // With buttons, more than four readings along the bottom wrap into rows of three, so every one stays big enough to tap.
  const footColumns = picking && footCells.length > 4
    ? 'repeat(3, minmax(0, 1fr))'
    : footCells.map(({ cell }) => `minmax(0, ${WEIGHT[cell.p] ?? 1}fr)`).join(' ');
  // Five readings in rows of three: blood pressure, the widest, takes two places so the rows come out even.
  const spanNibp = picking && footCells.length > 4 && (footCells.length + 1) % 3 === 0 && footCells.some(({ cell }) => cell.p === 'nibp');
  const tapOf = (cell: Cell) => {
    const r = pick ? regionFor.get(cell.region) : undefined;
    return r && pick ? { region: r, pick, revealed } : undefined;
  };

  return (
    <figure className="rx-monitor" role="group" aria-label={`${monitorLabel(o)}${picking && !revealed ? ' Tap the reading you want.' : ''}`} data-pick={picking ? '' : undefined}>
      <div className="rx-screen" aria-hidden={picking ? undefined : true}>
        <div className="rx-monitor-grid" data-traces={hasHr ? 'true' : 'false'}>
          {hasHr && <Traces hr={o.hr!} rr={num(o.rr) ? o.rr : 16} pleth={hasSpo2} rhythm={rhythm} />}
          {sideCells.length > 0 && (
            <div className="rx-side">
              {sideCells.map(({ cell, base }) => <CellView key={cell.p} cell={cell} base={base} refs={numberRefs} beat={cell.p === 'hr'} tap={tapOf(cell)} />)}
            </div>
          )}
          {footCells.length > 0 && (
            <div className="rx-foot" data-wrap={picking && footCells.length > 4 ? '' : undefined} style={{ gridColumn: '1 / -1', gridTemplateColumns: footColumns }}>
              {footCells.map(({ cell, base }) => <CellView key={cell.p} cell={cell} base={base} refs={numberRefs} tap={tapOf(cell)} span={spanNibp && cell.p === 'nibp' ? 2 : undefined} />)}
            </div>
          )}
        </div>
      </div>
      <figcaption className="rx-lip">
        <span className="rx-lip-led">Bedside observations</span>
      </figcaption>
    </figure>
  );
}

function CellView({ cell, base, refs, beat, tap, span }: {
  cell: Cell; base: number; refs: RefObject<Array<HTMLElement | null>>; beat?: boolean;
  tap?: { region: ObsRegion; pick: ObsPick; revealed: boolean }; span?: number;
}) {
  const reduce = useReducedMotion() ?? false;
  const state = tap ? tap.pick.stateOf(tap.region.id) : undefined;
  const mine = tap ? tap.pick.mine === tap.region.id : false;
  const share = tap && tap.revealed && tap.pick.pctOf ? tap.pick.pctOf(tap.region.id) : undefined;
  const mark = tap && tap.revealed && (state === 'correct' || state === 'wrong') ? state : undefined;
  const inner = (
    <>
      <span className="rx-cell-label">
        {mark && <span className="rx-cell-mark" data-tone={mark}>{mark === 'correct' ? <TickMark /> : <CrossMark />}</span>}
        <span>{chem(cell.label)}</span>
        {cell.sub && <small>{chem(cell.sub)}</small>}
        {beat && <span className="rx-beat" data-beat="" />}
        {tap?.revealed && mine && <span className="rx-cell-you" data-tone={state}>You</span>}
      </span>
      <span className="rx-cell-value" data-text={cell.text ? '' : undefined}>
        {cell.text ? chem(cell.values[0]) : cell.values.map((v, k) => (
          <span key={k}>{k > 0 && cell.joiner}<span ref={(el) => { refs.current[base + k] = el; }}>{v}</span></span>
        ))}
        {cell.unit && <small>{cell.unit}</small>}
      </span>
      {cell.map && <span className="rx-cell-map">{cell.map}</span>}
      {cell.note && <span className="rx-cell-note">{cell.note}</span>}
      {share !== undefined && (
        <span className="rx-cell-share" aria-hidden="true">
          <span className="rx-cell-share-bar"><motion.i initial={reduce ? false : { scaleX: 0 }} animate={{ scaleX: Math.max(0.02, share / 100) }} transition={{ type: 'spring', stiffness: 140, damping: 20, delay: 0.2 }} /></span>
          <b>{share}%</b>
        </span>
      )}
    </>
  );
  if (!tap) return <div className="rx-cell" data-p={cell.p} data-span={span}>{inner}</div>;
  const said = `${cell.spoken}.${tap.revealed && state === 'correct' ? ' A right answer.' : ''}${mine ? (tap.revealed ? ' Your pick.' : ' Locked in.') : ''}${share !== undefined ? ` ${share}% of the room.` : ''}`;
  return (
    <button type="button" className="rx-cell" data-p={cell.p} data-span={span} data-region={tap.region.param} data-option={tap.region.id} data-state={state} data-mine={mine ? '' : undefined}
      aria-label={said} aria-pressed={state === 'locked' || (mine && tap.revealed)} disabled={tap.pick.disabled} onClick={() => tap.pick.onPick?.(tap.region.id)}>
      <span aria-hidden="true" style={{ display: 'contents' }}>{inner}</span>
    </button>
  );
}

function TickMark() {
  return <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="3.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 12.8l4.6 4.6L19.5 7" /></svg>;
}
function CrossMark() {
  return <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="3.8" strokeLinecap="round"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11" /></svg>;
}

/** The ECG lane's share of the trace area when a pleth lane sits under it. */
export const ECG_SHARE = 0.6;
/** Each lane keeps a band at its top for its label (the trace never runs under it) and a little room at its foot (layout px). */
export const LANE_LABEL_BAND = { ecg: 22, pleth: 19 } as const;
const LANE_FOOT = 4;

/**
 * Where the ECG lane draws lead II: sized to its lane below the label as a monitor's auto-size does, so the tallest R wave and
 * the deepest S stay inside it whatever the rhythm, never larger than a 1 mV complex filling the lane.
 */
export function ecgLane(params: EcgParams, laneHeight: number): { gain: number; base: number } {
  return autoSize(traceExtent(params, 'II'), LANE_LABEL_BAND.ecg, laneHeight - LANE_FOOT, 0.9, 1);
}

/** The live traces: ECG lead II from the synthesiser at the patient's rate, and the pleth from the same beats. */
function Traces({ hr, rr, pleth, rhythm }: { hr: number; rr: number; pleth: boolean; rhythm?: Rhythm }) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const rate = clamp(Math.round(hr), 20, 250);
  const drawn: Rhythm = rhythm ?? (rate > 100 ? 'sinusTachy' : rate < 60 ? 'sinusBrady' : 'sinus');
  useEffect(() => {
    const b = box.current, c = canvas.current;
    if (!b || !c) return;
    const params: EcgParams = { rate, rhythm: drawn, stShift: {}, machineRead: [] };
    const stream = createEcgStream(params);
    const rr0 = 60 / rate;
    const traceColour = cssToken('--trace', '#3DF28A');
    const plethColour = cssToken('--pleth', '#3CC8F0');
    const dot = b.parentElement?.querySelector<HTMLElement>('[data-beat]') ?? null;
    return startSweep({
      canvas: c,
      box: b,
      secondsAcross: (w) => clamp(w / 52, 3.4, 7),
      lanes: ({ height }) => {
        const ecgH = pleth ? height * ECG_SHARE : height;
        const { gain, base } = ecgLane(params, ecgH);
        const lanes = [{ colour: traceColour, width: 1.9, y: (t: number) => clamp(base - stream.at(t, 'II') * gain, LANE_LABEL_BAND.ecg, ecgH - 2) }];
        if (pleth) {
          // The pleth fills its own lane below its label: foot at the bottom, the tallest pulse just under the band.
          const top = ecgH + LANE_LABEL_BAND.pleth, bottom = height - LANE_FOOT, room = bottom - top;
          const resp = (2 * Math.PI * clamp(rr, 4, 60)) / 60;
          lanes.push({
            colour: plethColour, width: 1.7,
            y: (t: number) => {
              const v = plethAt(t, stream.beats(t - 1.7, t + 0.001), rr0);
              return clamp(bottom - (0.04 + 0.78 * v + 0.04 * Math.sin(resp * t)) * room, top, bottom);
            },
          });
        }
        return lanes;
      },
      onFrame: (from, to) => {
        if (!dot || typeof dot.animate !== 'function') return;
        if (stream.beatTimes(from, to).length) dot.animate([{ opacity: 1, transform: 'scale(1.35)' }, { opacity: 0.3, transform: 'scale(1)' }], { duration: 280, easing: 'ease-out' });
      },
    });
  }, [rate, rr, pleth, drawn]);
  return (
    <div className="rx-traces" ref={box} data-rhythm={drawn} aria-hidden="true">
      <canvas ref={canvas} />
      <span className="rx-lane-label" style={{ top: 4, color: 'var(--trace, #3DF28A)' }}>II<small>25 mm/s</small></span>
      {pleth && <span className="rx-lane-label" style={{ top: `calc(${ECG_SHARE * 100}% + 3px)`, color: 'var(--pleth, #3CC8F0)' }}>Pleth</span>}
    </div>
  );
}
